import { addSeconds, endOfDay, format, getUnixTime, parseISO, startOfDay } from 'date-fns';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject, Subscription, fromEvent, of, timer } from 'rxjs';
import {
  catchError,
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  repeatWhen,
  retry,
  switchMap,
  take,
  takeUntil,
  timeout,
} from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

import { MxEventName, MxPropertyName } from '@axon-enterprise/track';
import { SnAbilityService } from '@lib/ability';
import {
  SnCamera,
  SnCameraManagementType,
  SnLiveVideoUrls,
  SnStreamSources,
} from '@lib/api/entities';
import { SnCamerasAPI, SnExternalAppliancesAPI } from '@lib/api/services';
import { SnCurrentOrganization, SnCurrentUser, SnSessionService } from '@lib/auth';
import { PursuitService } from '@lib/cameras/services/pursuit.service';
import { SnAspectRatio } from '@lib/common/enums';
import { isTimelineEvent } from '@lib/common/helpers';
import { ChannelsToShareDialogComponent } from '@lib/communication/twiliochat/components';
import { SnChannel } from '@lib/communication/twiliochat/models';
import { SnMessagesService } from '@lib/communication/twiliochat/services';
import { FsDataDogRumService } from '@lib/data-dog-rum';
import { MixpanelService } from '@lib/mixpanel';
import { ISnPlayer, SnPlayerState } from '@lib/player/common';
import {
  SnVideoPlayerAnalysisControl,
  SnVideoPlayerComponent,
  StreamAnalysisService,
  VideoPlaybackService,
} from '@lib/player/video-player';
import { SentrySearchFormDrawerComponent } from '@lib/sentry';
import { ITimeline, ITimelineDefinition, TimelineComponent, TimelineService } from '@lib/timeline';
import { FsLiveUpdateSwitchService } from '@lib/transport/live-update-switch';
import { SnDialogService } from '@lib/ui/dialog';
import { SnDrawerService } from '@lib/ui/drawer';
import { ViewListDialogComponent } from 'libs/operational-views/src/lib/components/view-list-dialog/view-list-dialog.component';
import { SnSldpPlayerReference } from 'libs/player/video-player/src/lib/references';
import { SnVideoPlayerType } from 'libs/player/video-player/src/lib/video-player.enums';
import { SnShareImageDialogComponent } from 'libs/ui/image-editor/src/lib/components';
import { SnLayoutService } from 'libs/ui/layout/src/lib/services';

import { CameraFormDrawerComponent } from '../camera-form-drawer/camera-form-drawer.component';
import { CameraReportDialogComponent } from '../camera-report-dialog/camera-report-dialog.component';
import { CameraSearchDialogComponent } from '../camera-search-dialog/camera-search-dialog.component';
import { FususCameraFormDialogComponent } from '../fusus-camera-form-dialog/fusus-camera-form-dialog.component';

const TIMELINE_CHANGE_DEBOUNCE_MS = 150;
const TIMELINE_SOURCE_LOCAL = 'local';
const TIMELINE_SOURCE_CLOUD = 'cloud';
@Component({
  selector: 'sn-camera-stream-view',
  templateUrl: './camera-stream-view.component.html',
  styleUrls: ['./camera-stream-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnCameraStreamViewComponent implements OnInit, OnDestroy, OnChanges {
  @ViewChild(SnVideoPlayerAnalysisControl) public videocontrols: SnVideoPlayerAnalysisControl;

  @Input()
  get camera(): SnCamera {
    return this._camera;
  }
  set camera(value: SnCamera) {
    this._camera = value;
    this.src = this._camera.liveVideoUrls;
    this.isUnmanagedStreamActive = this._camera.managementType === SnCameraManagementType.Unmanaged;
    this.isPlayingConfirmed = true;
    this.playStarted = false;
    this._hasTrackedPlaybackRequest = false;
    this.hasFmp4 = !!this.src?.main?.cloud?.fmp4;
    this.isFlv = !!this.src?.main?.cloud?.flv;
    this.isFususCamera = this._camera.isFususCamera;
    if (this.isQognify || (this._camera.type === 'PTZ' && this._canPtz)) {
      this.ptzConfig = {
        sessionID: this._camera?.path ? this._camera?.path.split('=')[1] : null,
        cameraID: this._camera?.externalCameraId ? this._camera?.externalCameraId : null,
        cameraName: this._camera.name,
        ptzMoveUrls:
          this._camera.discoveryUrls?.ptzMoveUrls ||
          this._camera.vendorApis?.cloud?.ptz?.ptzMoveUrls,
        deviceUrls:
          this._camera.discoveryUrls?.deviceUrls || this._camera.vendorApis?.cloud?.ptz?.deviceUrls,
        ptzInfo:
          this._camera.discoveryUrls?.ptzInfo || this._camera.vendorApis?.cloud?.ptz?.ptzInfo,
        ptzPresetCreate:
          this._camera.discoveryUrls?.ptzPresetCreate ||
          this._camera.vendorApis?.cloud?.ptz?.ptzPresetCreate,
        ptzPresetDelete:
          this._camera.discoveryUrls?.ptzPresetDelete ||
          this._camera.vendorApis?.cloud?.ptz?.ptzPresetDelete,
        ptzPresetList:
          this._camera.discoveryUrls?.ptzPresetList ||
          this._camera.vendorApis?.cloud?.ptz?.ptzPresetList,
        id: this._camera.id,
        onvifPort: this._camera.onvifPort,
        host: this._camera.onvifHost,
        username: this._camera.username,
        password: this._camera.password,
        vendorApis: this._camera.vendorApis,
        isAxis: this._camera.make?.toLowerCase() === 'axis',
        isHanwha: this._camera.make?.toLowerCase().includes('hanwha'),
        isSony: this._camera.make?.toLowerCase() === 'sony',
        isBosch: this._camera.make?.toLowerCase() === 'bosch',
        isQognify: this._camera.make?.toLowerCase() === 'qognify',
        isUbicquia: this._camera.make?.toLowerCase() === 'ubicquia',
        isCanon: ['canon', 'cannon'].includes(this._camera.make?.toLowerCase()),
        isAvigilon: this._camera.make?.toLowerCase()?.includes('avigilon'),
        isMilestone: this._camera.make?.toLowerCase() === 'milestone',
        isPanasonic: this._camera.make?.toLowerCase() === 'panasonic',
        isIPro:
          this._camera.make?.toLowerCase() === 'ipro' ||
          this._camera.make?.toLowerCase() === 'i-pro',
        path: this._camera.path,
        cameraMeta: this._camera.cameraMeta,
        ptzControlInvertX: this._camera.ptzControlInvertX,
        ptzControlInvertY: this._camera.ptzControlInvertY,
        ptzControlEnableArrows: this._camera.ptzControlEnableArrows,
        ptzControlZoomOnly: this._camera.ptzControlZoomOnly,
        make: this._camera.make?.toLowerCase(),
        external: this._camera.external,
        movementType: this._camera.ptzMovementType,
      };
    } else {
      this.ptzConfig = false;
    }
    this.isJpeg = this._checkIfImageSource(this.src?.main.cloud);
    this.frameSrc = this._genFrameSrc(this.src);
  }

  @Input()
  get camTime(): any {
    return this._camTime;
  }
  set camTime(value: any) {
    this._camTime = value;
    if (this._camTime && !this.cameraTimeline) {
      this._setCloudOrLocalTimeline();
    }
  }

  @Input()
  get timelineTime(): any {
    return this._camTime;
  }
  set timelineTime(value: any) {
    this._camTime = value;
    if (this._camTime) {
      this.setCamTime(this._camTime);
    }
  }

  @Input()
  get alwaysOn(): boolean {
    return this._alwaysOn;
  }
  set alwaysOn(value: boolean) {
    this._alwaysOn = value;
    if (this._alwaysOn && !this.cameraTimeline) {
      this._setCloudOrLocalTimeline();
    }
  }

  get playerRef(): ISnPlayer {
    return this._player;
  }

  get caption(): string {
    let caption = '';
    caption = caption.concat(this._camera.name);
    if (this._camera.location) {
      caption = caption.concat(': ', this._camera.location.name);
    }
    return caption;
  }

  get activeSegment(): ITimeline {
    return this._activeSegment;
  }
  get timelineZoom$(): Observable<d3.ZoomTransform> {
    return this._timelineZoom$.asObservable();
  }

  get isUbicquia(): boolean {
    return this._camera.make?.toLowerCase() === 'ubicquia';
  }

  get isExternalAvigilon(): boolean {
    return this._camera.external.externalType === 'avigilon';
  }

  get isQognify(): boolean {
    return this._camera.make?.toLowerCase() === 'qognify';
  }

  get isCitizen(): boolean {
    return this._camera.make?.toLowerCase() === 'citizen';
  }

  get isVerkadaCloudToCloud(): boolean {
    return this._camera.make?.toLowerCase() === 'verkadac2c';
  }

  get isOnMap(): boolean {
    return this._layout.isOnMap;
  }

  get isDigiZoomToggled(): boolean {
    return (
      (this.isDigiZoomActive &&
        !this.videoPlayer?.isClipping &&
        !this.localPlayerRef?.isClipping &&
        !this.cloudPlayerRef?.isClipping &&
        !this.ptzOn &&
        !this.unManagedPlayerRef?.isClipping) ||
      (this.isDigiZoomActive && this.isPtzDigiZoomEnabled)
    );
  }

  @ViewChildren(TimelineComponent) public timelineComponents: QueryList<TimelineComponent>;
  @ViewChild('recordingTimeline') public timelineComponent: TimelineComponent;
  @ViewChild('videoPlayer') public videoPlayer: SnVideoPlayerComponent;
  @ViewChild('localPlayerRef') public localPlayerRef: SnVideoPlayerComponent;
  @ViewChild('cloudPlayerRef') public cloudPlayerRef: SnVideoPlayerComponent;
  @ViewChild('unmanagedPlayerRef') public unManagedPlayerRef: SnVideoPlayerComponent;
  @ViewChild('citizenPlayer') public player: SnVideoPlayerComponent;

  @Input()
  public actions = true;

  @Input()
  public allowTimeout;

  @Input() public autoplay = true;
  @Input() public controls = true;
  @Input() public timestamp = true;
  @Input() public canEdit = false;
  @Input() public hoverActions = false;
  @Input() public operationalActions = false;
  @Input() public timelineHeight: number;
  @Input() public bulkSearch: boolean;
  @Input() public date = new Date();
  @Input() public isGridCam: boolean;
  @Input() public pauseOnTimeChange: boolean = false;

  @Output()
  public readonly streamPlay = new EventEmitter<boolean>();

  @Output()
  public readonly streamPause = new EventEmitter<void>();

  @Output()
  public readonly streamStop = new EventEmitter<void>();

  @Output()
  public readonly stateChange = new EventEmitter<SnPlayerState>();

  @Output()
  public readonly cameraSelect = new EventEmitter<SnCamera>();

  @Output()
  public readonly fullscreen = new EventEmitter<boolean>();

  @Output()
  public readonly timeline = new EventEmitter<ITimelineDefinition>();

  @Output()
  public readonly playbackReady = new EventEmitter<SnCamera>();

  @Output()
  public readonly currentTimelineTime = new EventEmitter<any>();

  @Output()
  public readonly selectedTime = new EventEmitter<any>();

  public readonly ASPECT_RATIO = SnAspectRatio;
  public readonly canAddWidget: boolean;
  public readonly canClip: boolean;
  public readonly canChange: boolean;

  public ptzConfig: any;
  public cloudTimeout: boolean;
  public isLocal: boolean;
  public isPursuit: boolean;
  public isUnmanagedStreamActive = false;
  public showCalendar: boolean = false;
  public hasFmp4: boolean;
  public urlUpdated: boolean;
  public showEvents: boolean;
  public isPlayingConfirmed = true;
  public localPlaying: boolean;
  public isFususCamera: boolean;
  public src: any;
  public form: UntypedFormGroup = new UntypedFormGroup({
    startTime: new UntypedFormControl(0),
    endTime: new UntypedFormControl(0),
    startDisplay: new UntypedFormControl('', Validators.required),
    endDisplay: new UntypedFormControl('', Validators.required),
  });
  public handleFullScreen = false;
  public start: Date;
  public end: Date;
  public time: Date;
  public isDragging: boolean;
  public toggleDisabledField: boolean = true;
  public cameraTimeline$: Observable<ITimelineDefinition>;
  public cameraTimeline: ITimelineDefinition;
  public startingTime: number = null;
  public isFullscreen = false;
  public rewindSpeedFactor: number;
  public isRewinding: boolean = false;
  public hasRecordings = this._hasRecordings.bind(this);
  public isDigiZoomActive: boolean = true;
  public videoElement: HTMLVideoElement;
  public ptzOn: boolean;
  public isJpeg: boolean = false;
  public frameSrc: string;
  public menuOpen: boolean;
  public isFlv: boolean;
  public getLabel = this._formatLabel.bind(this);
  public playStarted: boolean;
  public gotNewUrl: boolean;
  public showPlaybackMessage: boolean = false;
  public playbackSent = false;
  public previousTime = 0;
  public currentime;
  public timeobj: any = {};
  public isprevious: boolean = false;
  public loaded: boolean = false;
  public pausedTimeline;
  public pausedInterval;
  public allowAi: boolean;
  public timeoutStream: boolean = false;
  public isSessionActive: boolean = true;
  public isTimerInited: boolean = false;
  public isPtzDragging: boolean = false;
  public currentTime: number = 0;
  public showAvailableRecordings: boolean = false;
  public isPtzDigiZoomEnabled: boolean = false;
  public switchedToPrevious: boolean;
  public isLoadingTimeline: boolean = false;
  private _camera: SnCamera;
  private _alwaysOn: boolean;
  private _camTime: any;
  private _canPtz: boolean;
  private _player: ISnPlayer;
  private readonly _destroy$ = new Subject<void>();
  private readonly _initialize$ = new Subject<void>();
  private readonly _startTimer$ = new Subject<void>();
  private readonly _stopTimer$ = new Subject<void>();
  private _startTime: Date;
  private _endTime: Date;
  private _activeSegment: ITimeline;
  private _timelineZoom$ = new Subject<d3.ZoomTransform>();
  private _switchedTimeline = false;
  private _cloudVideoTimeout;
  private _streetViewDialogRef: MatDialogRef<CameraReportDialogComponent>;
  private _timelineUpdateInterval;
  private _zoomPosition = { x: 0, y: 0 };
  private _zoomTarget = { x: 0, y: 0 };
  private _zoomPoint = { x: 0, y: 0 };
  private _scale = 1;
  private _digiZoomInited: boolean = false;
  private readonly _destroyDigiZoom$ = new Subject<void>();
  private readonly _changeTimeDebounce$ = new Subject<{ timeInTimeline: number }>();
  private _isUserSeeking = false;
  private _fragmentChangeSubscription: Subscription | null = null;
  private _liveUpdateSubscriptionId: string | null = null;
  private _timelineScrubbingStartTime: number | null = null;
  private _fullscreenPlaybackStartTime: number | null = null;
  private _hasTrackedPlaybackRequest = false;

  constructor(
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _chatMessageService: SnMessagesService,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _dialog: MatDialog,
    private readonly _currentUser: SnCurrentUser,
    private readonly _currentOrganization: SnCurrentOrganization,
    private readonly _externalAppliancesAPI: SnExternalAppliancesAPI,
    private readonly _camerasAPI: SnCamerasAPI,
    private readonly _ability: SnAbilityService,
    public readonly _toast: ToastrService,
    public readonly _drawer: SnDrawerService,
    private readonly _router: Router,
    private readonly _timelineService: TimelineService,
    private readonly _playbackSerivce: VideoPlaybackService,
    private readonly _layout: SnLayoutService,
    private readonly _matDialog: MatDialog,
    private readonly _dialogService: SnDialogService,
    private readonly _datadogService: FsDataDogRumService,
    private readonly _liveUpdateSwitchService: FsLiveUpdateSwitchService,
    private readonly _pursuitService: PursuitService,
    private readonly _sessionService: SnSessionService,
    private readonly _mixpanelService: MixpanelService,
    private readonly _streamAnalysisService: StreamAnalysisService,
  ) {
    this.allowAi = this._currentOrganization.plan.allowAi;
    this.isPursuit = this._pursuitService.getPursuitValue();
    this.canAddWidget = this._ability.can({ subject: 'dashboards/widget', action: 'add' });
    const ptzAction = this._currentOrganization.additionalData?.disablePtz ? 'change' : 'view';
    this._canPtz = this._ability.can({ subject: 'cameras/camera', action: ptzAction });
    this.canClip = this._ability.can({ subject: 'cameras/camera', action: 'clip_recordings' });
    this.canChange = this._ability.can({ subject: 'cameras/camera', action: 'change' });
    this._cloudVideoTimeout = this._currentOrganization.settings.cloudVideoTimeout;
    this.fullscreen.pipe(takeUntil(this._destroy$)).subscribe((value: boolean) => {
      if (!value && this._streetViewDialogRef) {
        this._streetViewDialogRef.close();
      }
    });
  }

  @HostListener('document:click', ['event'])
  @HostListener('document:mousedown', ['$event'])
  @HostListener('document:touchstart', ['$event'])
  @HostListener('document:mouseup', ['$event'])
  @HostListener('document:touchend', ['$event'])
  @HostListener('document:contextmenu', ['$event'])
  @HostListener('window:mousemove', ['$event'])
  @HostListener('window:touchmove', ['$event'])
  @HostListener('window:keydown', ['$event'])
  @HostListener('window:keyup', ['$event'])
  @HostListener('wheel', ['$event'])
  public resetTimer(event: MouseEvent | TouchEvent | WheelEvent): void {
    if (
      this._currentUser.isVideoWallUser ||
      this.timeoutStream ||
      !this.isFullscreen ||
      this.isGridCam ||
      this.isLocal
    ) {
      return;
    }
    this._stopTimer$.next();
    this._startTimer$.next();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    if (!this.isFullscreen || this.isGridCam || this.isLocal) {
      this._stopTimer$.next();
    }
  }

  get isCitizenFastForwarding(): boolean {
    return this.player?.reference?.isFastForwarding;
  }

  get isCitizenRewinding(): boolean {
    return this.player?.reference?.isRewinding;
  }

  get playbackRate(): number {
    return this.player?.reference?.playbackRate;
  }

  get citizenRewindSpeedFactor(): number {
    return this.player?.reference?.rewindSpeedFactor;
  }

  public citizenScreenshot(): void {
    const video = this.player.elementRef.nativeElement.querySelector('video');
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    const dialogRef = this._matDialog.open(SnShareImageDialogComponent, {
      data: {
        url: canvas.toDataURL(),
        playerRef: this.player,
      },
    });
    dialogRef.afterClosed().pipe(takeUntil(this._destroy$)).subscribe();
  }

  public onCitizenFullscreenChange(fullscreen: boolean): void {
    this.isFullscreen = fullscreen;
    if (this.isFullscreen) {
      setTimeout(() => {
        this._initDigiZoom(100, 0.1);
      }, 500);
    } else {
      this._resetDigiZoom();
    }
  }

  public back(amount: number): void {
    if (this.player) {
      this.player.back(amount);
    }
  }

  public forward(amount: number): void {
    if (this.player) {
      this.player.forward(amount);
    }
  }

  public rewind(): void {
    if (this.player) {
      this.player.reference.rewind();
    }
  }

  public fastForward(): void {
    if (this.player) {
      this.player.reference.fastForward();
    }
  }

  public sharedIn(): boolean {
    return this.camera?.shareInfo?.direction === 'In';
  }

  public onBrush(range: Date[]): void {
    if (range) {
      this.start = range[0] || null;
      this.end = range[1] || null;
      this.form.patchValue({
        startTime: this.start.getTime(),
        startDisplay: this.start.toLocaleTimeString(),
        endTime: this.end.getTime(),
        endDisplay: this.end.toLocaleTimeString(),
      });
    }
  }

  public openOpGridDialog(): void {
    this._dialog
      .open(CameraSearchDialogComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((camera) => {
        if (camera) {
          this.cameraSelect.next(camera);
        }
      });
  }

  @HostListener('mouseover')
  public mouseOver(): void {
    if (this.hoverActions) {
      this.actions = true;
    }
  }

  @HostListener('mouseleave', ['$event'])
  public mouseOut(event: MouseEvent): void {
    if (this.hoverActions) {
      this.actions = false;
      this._changeDetectorRef.detectChanges();
    }
  }

  public goToCamera(camera: SnCamera): void {
    this._router.navigate(['map/camera/', camera.id]);
  }

  public toggleCalendar(): void {
    this.showCalendar = !this.showCalendar;
  }

  public activateSegment(timeline: ITimeline): void {
    if (this._activeSegment && timeline && timeline.start === this._activeSegment.start) {
      return;
    }
    this._switchedTimeline = true;
    this._activeSegment = timeline;
    this.toggleDisabledField = false;
    this._player.live = false;
    if (this.hasFmp4 || this.isFlv || !this._activeSegment) {
      if (this.bulkSearch) {
        this.showPlaybackMessage = true;
        this.playbackReady.next(this._camera);
      }
      return;
    }
    if (!this.camera.isFususCamera) {
      const start = Math.max(this._activeSegment.start, startOfDay(this.date).getTime() / 1000);
      this._startTime = new Date(Math.ceil(start) * 1000);
      this._endTime = new Date(Math.floor(this._activeSegment.end) * 1000);
      this.src =
        this.camera.isManaged && !this.isExternal
          ? new SnLiveVideoUrls({
              main: this.camera.playbackUrls.main.toStreamSourceListFmp4(
                null,
                this._startTime,
                this._endTime,
              ),
            })
          : new SnLiveVideoUrls({
              main: this.camera.playbackUrls.main.toStreamSourceList(
                null,
                this._startTime,
                this._endTime,
              ),
            });
      this._changeDetectorRef.detectChanges();
    } else {
      const start = Math.max(this._activeSegment.start, startOfDay(this.date).getTime() / 1000);
      this._startTime = new Date(Math.ceil(start) * 1000);
      this._endTime = new Date(Math.floor(this._activeSegment.end) * 1000);
      this.src = new SnLiveVideoUrls({
        main: this.camera.playbackUrls.main.toFususStreamSourceList(
          this._activeSegment.file_path || this._activeSegment.path,
          this._startTime,
        ),
        sub: this.camera.playbackUrls.main.toFususStreamSourceList(
          this._activeSegment.file_path || this._activeSegment.path,
          this._startTime,
        ),
      });
      this._changeDetectorRef.detectChanges();
    }
    this.startingTime = null;
    this.showPlaybackMessage = false;
  }

  public moveCursor(date: Date): void {
    if (this.camera?.id) {
      this._timelineScrubbingStartTime = Date.now();
      const streamContext = this._streamAnalysisService.getStreamContextStart(this.camera);
      const metricData = {
        id: this.camera.id,
        camera_id: this.camera.id,
        timestamp: this._timelineScrubbingStartTime,
        source_ui: 'timeline_scrubbing',
        ...streamContext,
      };

      this._datadogService.addAction('playback_attempt_start', {
        ...metricData,
      });

      if (this.isFullscreen && !this._hasTrackedPlaybackRequest) {
        this._mixpanelService.trackMetric(MxEventName.PlaybackStartReqCamAf, {
          [MxPropertyName.FususPlaybackStartUi]: 'Livestream Full Screen',
          [MxPropertyName.FususPlaybackId]: this.camera.id,
        });
        this._hasTrackedPlaybackRequest = true;
      }
    }

    switch (true) {
      case this._sessionService.isFederal:
        this._startTime = date;
        this._endTime = new Date(this._activeSegment.end * 1000);
        this.src = new SnLiveVideoUrls({
          main: this.camera.playbackUrls.main.toFederalStreamSourceList(
            this._startTime,
            this._endTime,
          ),
          sub: this.camera.playbackUrls.sub.toFederalStreamSourceList(
            this._startTime,
            this._endTime,
          ),
        });
        break;
      case this.hasFmp4:
        this._startTime = date;
        this._endTime = new Date(this._activeSegment.end * 1000);
        this.src = new SnLiveVideoUrls({
          main: this.camera.playbackUrls.main.toAvigilonStreamSourceList(this._startTime),
          sub: this.camera.playbackUrls.sub.toAvigilonStreamSourceList(this._startTime),
        });
        break;
      case this.isFlv:
        this._startTime = date;
        this._endTime = new Date(this._activeSegment.end * 1000);
        const baseUrl = this.camera.prerecordingUrls.video.split('&')[0];
        const authKey = this.camera.prerecordingUrls.headers['Cookie']
          ?.split('; ')
          ?.find((value) => value?.includes('auth_key'))
          .split('=')[1];
        const startDate = format(
          new Date(date.getTime() + date.getTimezoneOffset() * 60000),
          'yyyyMMddHHmmss.SSS',
        );
        const endDate = format(
          new Date(this._endTime.getTime() + this._endTime.getTimezoneOffset() * 60000),
          'yyyyMMddHHmmss.SSS',
        );
        this.src = `${baseUrl}&start_timestamp=${startDate}&end_timestamp=${endDate}&A=${authKey}`;
        break;
    }
    if (!this._switchedTimeline && this._startTime) {
      const timeInTimeline = (date.getTime() - this._startTime.getTime()) / 1000;
      if (this.isDragging) {
        this._changeTimeDebounce$.next({ timeInTimeline });
      } else {
        this._player.changeCurrentTime(timeInTimeline);
      }
    }
    this.startingTime = null;
    this.time = date;
    this.selectedTime.next(this.time);

    if (this.ptzConfig) {
      setTimeout(() => {
        this.isPtzDigiZoomEnabled = true;
      }, 500);
    }
    if (!this.isDragging && !this.timelineTime) {
      this._player.play();
      this._changeDetectorRef.detectChanges();
    }
    this.switchedToPrevious = true;
  }

  public setCamTime(date: Date): void {
    if (!this._player) {
      setTimeout(() => {
        this._loadCameraTime(date);
      }, 800);
      return;
    }
    this._loadCameraTime(date);
  }

  public dragging(isDragging: boolean): void {
    this.isDragging = isDragging;
    if (this.isDragging) {
      this._player.pause();
    }
  }

  public onTimelineZoom(event: d3.ZoomTransform): void {
    this._timelineZoom$.next(event);
  }

  public onUserSeeking(isUserSeeking: boolean): void {
    if (isUserSeeking) {
      this._setUserSeekingState();
    } else {
      this._isUserSeeking = false;
    }
  }

  private _setUserSeekingState(): void {
    this._isUserSeeking = true;
    this._setupFragmentChangedListener();
  }

  private _setupFragmentChangedListener(): void {
    this._cleanupFragmentSubscription();

    if (!this._player?.reference) {
      return;
    }

    // Only set up listener for HLS players
    if (this._player.reference.type === SnVideoPlayerType.HLS) {
      const fragChanged$ = this._player.reference.fragChanged$;
      if (fragChanged$) {
        this._fragmentChangeSubscription = fragChanged$.pipe(takeUntil(this._destroy$)).subscribe({
          next: () => {
            this._isUserSeeking = false;
          },
        });
      }
    }
  }

  private _cleanupFragmentSubscription(): void {
    if (this._fragmentChangeSubscription) {
      this._fragmentChangeSubscription.unsubscribe();
      this._fragmentChangeSubscription = null;
    }
  }

  public ngOnInit(): void {
    this._changeTimeDebounce$
      .pipe(
        debounceTime(TIMELINE_CHANGE_DEBOUNCE_MS),
        distinctUntilChanged(
          (prev, curr) => Math.abs(prev.timeInTimeline - curr.timeInTimeline) < 0.5,
        ),
        takeUntil(this._destroy$),
      )
      .subscribe(({ timeInTimeline }) => {
        this._player.changeCurrentTime(timeInTimeline);
      });

    this.cloudTimeout =
      !this._currentUser.isVideoWallUser && this.allowTimeout && !this._currentUser.isSupportUser;
    this._initLiveUpdate();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.timelineTime) {
      this.loaded = false;
    }
    this._changeDetectorRef.detectChanges();
  }

  public ngOnDestroy(): void {
    if (!this._player?.live && !this._player?.reference.loaded) {
      this._emitPlaybackAttemptEnd('canceled');
    }

    this._unsubscribeFromLiveUpdate();
    this._cleanupFragmentSubscription();
    this.streamStop.next();
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _emitPlaybackAttemptEnd(
    status: 'success' | 'fail' | 'canceled',
    error?: {
      raw_error?: string;
      error_code?: string;
      error_source?: string;
      is_canceled?: boolean;
    },
  ): void {
    if (!this.camera?.id) {
      return;
    }

    const baseMetricData = {
      id: this.camera.id,
      camera_id: this.camera.id,
      status,
      ...(status === 'canceled' && { is_canceled: true }),
      ...error,
    };

    if (this._timelineScrubbingStartTime) {
      const streamContext = this.isLocal
        ? this._streamAnalysisService.getStreamContextEnd(this.camera, false)
        : this._streamAnalysisService.getStreamContextEnd(this.camera, true);
      this._datadogService.addAction('playback_attempt_end', {
        duration_ms: Date.now() - this._timelineScrubbingStartTime,
        ...baseMetricData,
        ...streamContext,
      });
      this._timelineScrubbingStartTime = null;
    }

    if (this._fullscreenPlaybackStartTime) {
      const streamContext = this.isLocal
        ? this._streamAnalysisService.getStreamContextEnd(this.camera, false)
        : this._streamAnalysisService.getStreamContextEnd(this.camera, true);
      this._datadogService.addAction('playback_attempt_end', {
        ...baseMetricData,
        duration_ms: Date.now() - this._fullscreenPlaybackStartTime,
        ...streamContext,
      });
      this._fullscreenPlaybackStartTime = null;
    }
  }

  public openSentrySearch(): void {
    const aiCamera = [this._camera];
    this._drawer.open(SentrySearchFormDrawerComponent, {
      viewContainerRef: this._viewContainerRef,
      data: aiCamera,
      closeOnNavigation: true,
      hasBackdrop: false,
      panelClass: ['hide-on-fullscreen'],
    });
  }

  get isExternal(): boolean {
    if (this.camera) {
      return this.camera?.managementType === SnCameraManagementType.External;
    }
  }

  public confirmPlaying(): void {
    this._toggleConfirmAlertVisibility(true);
    this._player.play();
  }

  public toggleFullscreen(ptzOn?: boolean): void {
    this._player.changeFullscreen(ptzOn);
  }

  public onStateChange(state: SnPlayerState): void {
    this.stateChange.next(state);
  }

  public externalStateChange(state: SnPlayerState): void {
    if (state === SnPlayerState.Ended) {
      this._externalAppliancesAPI
        .checkStatus(this.camera.externalAppliance)
        .pipe(
          catchError(() => this._externalAppliancesAPI.checkStatus(this.camera.externalAppliance)),
          switchMap(() => {
            return this._camerasAPI.getCamera(this.camera.id);
          }),
          takeUntil(this._destroy$),
        )
        .subscribe((camera) => {
          this.camera = camera;
        });
    }
  }

  public dateChange(): void {
    if (this._activeSegment) {
      this._activeSegment = null;
      this.timelineComponent.deactivateSegment();
    }
  }

  public onPlayerInit(player: ISnPlayer): void {
    this._initialize$.next();
    this._player = player;
    if (this.rewindSpeedFactor) {
      player.reference.rewindSpeedFactor = this.rewindSpeedFactor;
    }
    if (this._switchedTimeline && this.time && this._startTime) {
      const timeInTimeline = (this.time.getTime() - this._startTime.getTime()) / 1000;
      player.changeCurrentTime(timeInTimeline);
      this._switchedTimeline = false;
    }
    player.play$.pipe(takeUntil(this._initialize$), takeUntil(this._destroy$)).subscribe(() => {
      this._onLiveStreamSuccess();

      if (this.pausedTimeline) {
        this.pausedTimeline = false;
        this.pausedInterval.unsubscribe();
        this._changeDetectorRef.detectChanges();
      }
      this.playStarted = true;
      this.streamPlay.emit(player.live);
      if (this.isRewinding) {
        this._player.reference.fragLoading = false;
        this._player.reference.rewind(this.rewindSpeedFactor);
      }
      if (this._camera.isLte) {
        this._initLTETimer();
      }
      if (this.ptzConfig && player.live) {
        this.isPtzDigiZoomEnabled = false;
      }

      if (this.isFullscreen) {
        this._initDigiZoom(100, 0.1);
      }
    });

    player.pause$.pipe(takeUntil(this._initialize$), takeUntil(this._destroy$)).subscribe(() => {
      if (this.isRewinding) {
        this._player.reference.fragLoading = false;
        this._player.reference.rewind(this.rewindSpeedFactor);
      }
      if (this.ptzConfig) {
        this.isPtzDigiZoomEnabled = true;
      }
      if (player.live && document.hasFocus()) {
        this.pausedInterval = of(true)
          .pipe(delay(60000), takeUntil(this._destroy$), takeUntil(player.live$))
          .subscribe(() => {
            this._findAndSetPauseTime();
          });
      }

      this.streamPause.emit();
    });

    player.end$.pipe(takeUntil(this._initialize$), takeUntil(this._destroy$)).subscribe(() => {
      this.streamStop.emit();
      this.playStarted = false;
    });

    player.ended$.pipe(takeUntil(this._initialize$), takeUntil(this._destroy$)).subscribe((res) => {
      if (this.cameraTimeline && this.timelineComponent && this._activeSegment) {
        const timelines = this.cameraTimeline?.timeline;
        const end = this._activeSegment.end;
        const nextSegment = timelines.find((timeline: ITimeline) => {
          return timeline.start > end;
        });
        if (nextSegment) {
          const currentDate = !this._activeSegment
            ? endOfDay(new Date())
            : endOfDay(new Date(this._activeSegment.end * 1000));
          const newDate = endOfDay(new Date(nextSegment.end * 1000));
          const isNextDay = currentDate.getTime() < newDate.getTime();
          if (isNextDay) {
            this.date = startOfDay(new Date(nextSegment.end * 1000));
          }
          this.activateSegment(nextSegment);
          this.moveCursor(this._startTime);
        } else {
          if (!this._player['isBosch']) {
            this._player.src = this._camera.liveVideoUrls;
          }
          this._player.live = true;
        }
      }
    });

    if (!this.isJpeg) {
      player.currentTime$
        .pipe(
          takeUntil(this._initialize$),
          takeUntil(player.reference.destroyed$),
          takeUntil(this._destroy$),
        )
        .subscribe((time) => {
          if (this.cameraTimeline && this.timelineComponent) {
            this._updateTime(time);
            if (player.live) {
              if (this.hasFmp4) {
                this.cameraTimeline[0].record[this.cameraTimeline[0].record.length - 1].end =
                  new Date(Math.floor(this.time.getTime())).toISOString();
              } else {
                this.cameraTimeline.timeline[this.cameraTimeline.timeline.length - 1].end =
                  Math.floor(this.time.getTime() / 1000);
              }
            }
            this._changeDetectorRef.detectChanges();
          }
        });

      player.live$.pipe(takeUntil(this._destroy$)).subscribe((live) => {
        if (live && this.ptzConfig) {
          this.isPtzDigiZoomEnabled = false;
        }
        if (live && this.timelineComponent) {
          this.switchedToPrevious = false;
          this.time = new Date();
          this.startingTime = null;
          this.date = startOfDay(new Date());
          if (this._activeSegment) {
            this._activeSegment = null;
            this.timelineComponent.deactivateSegment();
          }
          this.timelineComponent.resetTransform();
        }
      });
    }

    player.ptzOn$?.pipe(takeUntil(this._destroy$))?.subscribe((ptzOn) => {
      this.ptzOn = ptzOn;
    });

    player.connected$?.pipe(takeUntil(this._destroy$))?.subscribe((connection) => {
      if (!connection && this.playStarted && !this.urlUpdated) {
        this._getNewUrl();
      }
    });

    player.loaded$?.pipe(distinctUntilChanged(), takeUntil(this._destroy$)).subscribe((loaded) => {
      if (loaded && !this._player.live) {
        this._emitPlaybackAttemptEnd('success');
      }
      if (!this.loaded) {
        this.playbackReady.next(this._camera);
        this.loaded = true;
      }
    });

    player.reference?.rewindSpeedFactorChange$
      .pipe(
        takeUntil(this._initialize$),
        takeUntil(player.reference.destroyed$),
        takeUntil(this._destroy$),
      )
      .subscribe((count) => {
        this.rewindSpeedFactor = count;
      });

    player.reference?.rewinding$
      .pipe(
        takeUntil(this._initialize$),
        takeUntil(player.reference.destroyed$),
        takeUntil(this._destroy$),
      )
      .subscribe(() => {
        const loadPrevious = this.switchedToPrevious
          ? Math.floor(this.currentTime) <= 0
          : Math.floor(this.currentTime) <= Math.floor(this.startingTime);
        if (loadPrevious) {
          this.switchedToPrevious = true;
          this._player.pause();
          const timelines = this.cameraTimeline?.timeline;
          if (!timelines?.length) {
            return;
          }
          const index = !this._activeSegment
            ? timelines.length - 1
            : timelines.findIndex((timeline) => {
                return (
                  timeline.start === Math.ceil(this._activeSegment.start) &&
                  timeline.end === Math.ceil(this._activeSegment.end)
                );
              }) - 1;
          const segment = timelines[index];
          if (segment) {
            const currentDate = !this._activeSegment
              ? endOfDay(new Date())
              : endOfDay(new Date(this._activeSegment.end * 1000));
            const newDate = endOfDay(new Date(segment.end * 1000));
            const isPreviousDay = currentDate.getTime() > newDate.getTime();
            if (isPreviousDay) {
              this.date = startOfDay(new Date(segment.end * 1000));
            }
            this._player.reference.fragLoading = true;
            this.activateSegment(segment);
            this.moveCursor(this._endTime);
          }
        }
      });

    this._playbackSerivce.rewind$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.isRewinding = true;
      this._player.live = false;
    });

    this._playbackSerivce.rewindStop$.pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.isRewinding = false;
      this._changeDetectorRef.detectChanges();
    });

    this._setupFragmentChangedListener();

    this._changeDetectorRef.detectChanges();
  }

  public onLocalPlayerInit(player: ISnPlayer): void {
    player.connected$.pipe(takeUntil(this._destroy$)).subscribe((connected) => {
      if (connected) {
        this.localPlaying = true;
        this.onPlayerInit(player);
        this._setActivePlayer(player, true);
        if (this.cloudPlayerRef) {
          this._setActivePlayer(this.cloudPlayerRef, false);
        }
      }
    });
  }

  public onCloudPlayerInit(player: ISnPlayer): void {
    player.connected$.pipe(takeUntil(this._destroy$)).subscribe((connected) => {
      if (connected) {
        if (!this.localPlaying) {
          this.onPlayerInit(player);
          this._setActivePlayer(player, true);
        } else {
          this._setActivePlayer(player, false);
        }
        this._changeDetectorRef.detectChanges();
      }
    });
  }

  public onPlayerError(player: ISnPlayer): void {
    this._player = player;
    if (!player.live) {
      this._emitPlaybackAttemptEnd('fail', {
        raw_error: 'Player error occurred',
        error_code: 'PLAYER_ERROR',
        error_source: 'camera_stream_view',
      });
    }

    this._setupFragmentChangedListener();
    this._setCloudOrLocalTimeline();
    this.showAvailableRecordings = true;
  }

  public hotPursuit(camera: SnCamera): void {
    if (this.time) {
      this._pursuitService.selectedTime = this.time;
    }
    window.open(`/overwatch/${camera.id}`);
  }

  public editCamera(data: SnCamera): void {
    if (!data?.isFususCamera) {
      this._drawer
        .open<CameraFormDrawerComponent, SnCamera, SnCamera>(CameraFormDrawerComponent, {
          viewContainerRef: this._viewContainerRef,
          hasBackdrop: false,
          width: 320,
          data,
        })
        .afterClosed$.pipe(
          filter((targetCamera: SnCamera) => !!targetCamera),
          takeUntil(this._destroy$),
        )
        .subscribe((response) => {
          if (response) {
            this._camerasAPI.setTargetCamera(response);
          }
        });
    }

    if (data?.isFususCamera) {
      this._loadCameraSettings(data).subscribe((res) => {
        this._matDialog.open(FususCameraFormDialogComponent, {
          data: {
            cameraSettings: res,
            camera: data,
          },
          height: 'auto',
          width: '90%',
        });
      });
    }
  }

  public openChannel(): void {
    const dialogRef = this._dialog.open(ChannelsToShareDialogComponent);

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((channel) => {
        if (channel?.twilioChannel) {
          this._shareTo(channel);
        }
      });
  }

  public onFullscreenChange(fullscreen: boolean): void {
    this.isFullscreen = fullscreen;
    this.fullscreen.next(this.isFullscreen);
    this._changeDetectorRef.markForCheck();
    if (this.isFullscreen) {
      if (this.camera?.id && !this._player.live) {
        this._fullscreenPlaybackStartTime = Date.now();
        const streamContext = this._streamAnalysisService.getStreamContextStart(this.camera);
        const metricData = {
          id: this.camera.id,
          camera_id: this.camera.id,
          timestamp: this._fullscreenPlaybackStartTime,
          source_ui: 'fullscreen_playback',
          ...streamContext,
        };

        this._datadogService.addAction('playback_attempt_start', {
          ...metricData,
        });
      }
      this._setCloudOrLocalTimeline();
      setTimeout(() => {
        this._initDigiZoom(100, 0.1);
      }, 500);
      if (!this.isLocal) {
        if (!this.isTimerInited) {
          this._initTimeoutTimer();
        } else {
          this._startTimer$.next();
        }
      }
    } else {
      if (this._fullscreenPlaybackStartTime) {
        this._emitPlaybackAttemptEnd('canceled');
      }

      this._hasTrackedPlaybackRequest = false;

      clearInterval(this._timelineUpdateInterval);
      if (!this._alwaysOn) {
        this.cameraTimeline = null;
      }
      this._resetDigiZoom();
      if (!this.isLocal) {
        this._stopTimer$.next();
      }
    }
  }

  public reportCameraIssue(cameraId: string): void {
    this._streetViewDialogRef = this._dialog.open(CameraReportDialogComponent, {
      data: cameraId,
    });
    this._streetViewDialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._streetViewDialogRef = null;
      });
  }

  public addToDashboard(): void {
    this._dialog.open(ViewListDialogComponent, { data: this.camera });
  }

  public getFullScreen(event: boolean): void {
    this.handleFullScreen = event;
    this._changeDetectorRef.detectChanges();
  }

  public onExternalError(event: any): void {
    this._externalAppliancesAPI
      .checkStatus(this.camera.externalAppliance)
      .pipe(
        switchMap(() => {
          return this._camerasAPI.getCamera(this.camera.id);
        }),
        takeUntil(this._destroy$),
      )
      .subscribe((camera) => {
        this.camera = camera;
      });
  }

  public toggleDigiZoom(): void {
    this.isDigiZoomActive = !this.isDigiZoomActive;
  }

  public screenshot(): void {
    const video = this.cloudPlayerRef.elementRef.nativeElement.querySelector('video');

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
    const dialogRef = this._matDialog.open(SnShareImageDialogComponent, {
      data: {
        url: canvas.toDataURL(),
        playerRef: this.cloudPlayerRef,
      },
    });
    dialogRef.afterClosed().pipe(takeUntil(this._destroy$)).subscribe();
  }

  public createClip(camera: SnCamera): void {
    this._mixpanelService.trackMetric(MxEventName.ClipCreationStartCamAf, {});

    if (camera.incidentId) {
      window.open(`/recordings-panel/${camera.id}/?incidentId=${camera.incidentId}`);
    } else {
      window.open(`/recordings-panel/${camera.id}`);
    }
  }

  public reinitialize(): void {
    this._player.initialization();
    this._changeDetectorRef.detectChanges();
    this.isSessionActive = true;
    this.timeoutStream = false;
    this._changeDetectorRef.detectChanges();
    if (this.isFullscreen) {
      this._startTimer$.next();
    }
    this._changeDetectorRef.detectChanges();
  }

  public onPtzDrag(dragging: boolean): void {
    this.isPtzDragging = dragging;
  }

  public retryTimeline(): void {
    this._setCloudOrLocalTimeline();
  }

  public openCitizenPopup(): void {
    this._camerasAPI.setTargetCamera(this._camera);
  }

  private _resetDigiZoom(): void {
    this._zoomPosition = { x: 0, y: 0 };
    this._zoomPoint = { x: 0, y: 0 };
    this._scale = 1;
    this._digiZoomInited = false;
    this._destroyDigiZoom$.next();
    if (this.videoElement) {
      this.videoElement.style.transform = 'translate(0px, 0px) scale(1, 1)';
    }
  }

  private _onLiveStreamSuccess(): void {
    this._datadogService.addAction('live_stream_started', {
      id: this.camera.id,
      name: this.camera.name,
    });
  }

  private _initDigiZoom(maxScale: number, step: number): void {
    if (!this.isFullscreen) {
      return;
    }
    if (this.isUnmanagedStreamActive) {
      this.videoElement = this.unManagedPlayerRef?.video;
    } else {
      this.videoElement = this.localPlaying
        ? this.localPlayerRef?.video
        : this.cloudPlayerRef?.video;
    }
    if (!this.videoElement) {
      return;
    }
    const size = { w: this.videoElement.clientWidth, h: this.videoElement.clientHeight };
    this.videoElement.style.transformOrigin = '0 0';
    this.videoElement.style.transform = `translate(${this._zoomPosition.x}px, ${this._zoomPosition.y}px) scale(${this._scale}, ${this._scale})`;
    if (this._digiZoomInited) {
      this._destroyDigiZoom$.next();
      this._digiZoomInited = false;
    }

    fromEvent(window, 'wheel')
      .pipe(
        takeUntil(this._destroyDigiZoom$),
        takeUntil(this._initialize$),
        takeUntil(this._destroy$),
      )
      .subscribe((event) => {
        if (!this.isDigiZoomToggled || !this.isFullscreen) {
          return;
        }

        if (isTimelineEvent(event.target)) {
          return;
        }

        scrolled(event);
      });

    const update = () => {
      this.videoElement.style.transform = `translate(${this._zoomPosition.x}px, ${this._zoomPosition.y}px) scale(${this._scale}, ${this._scale})`;
    };

    const scrolled = (e) => {
      this._zoomPoint.x = e.clientX - this.videoElement.offsetLeft;
      this._zoomPoint.y = e.clientY - this.videoElement.offsetTop;

      let delta = e.delta || e.wheelDelta;
      if (delta === undefined) {
        delta = e.detail;
      }
      delta = Math.max(-1, Math.min(1, delta));

      this._zoomTarget.x = (this._zoomPoint.x - this._zoomPosition.x) / this._scale;
      this._zoomTarget.y = (this._zoomPoint.y - this._zoomPosition.y) / this._scale;

      this._scale += delta * step * this._scale;
      this._scale = Math.max(1, Math.min(maxScale, this._scale));

      this._zoomPosition.x = -this._zoomTarget.x * this._scale + this._zoomPoint.x;
      this._zoomPosition.y = -this._zoomTarget.y * this._scale + this._zoomPoint.y;

      if (this._zoomPosition.x > 0) {
        this._zoomPosition.x = 0;
      }
      if (this._zoomPosition.x + size.w * this._scale < size.w) {
        this._zoomPosition.x = -size.w * (this._scale - 1);
      }
      if (this._zoomPosition.y > 0) {
        this._zoomPosition.y = 0;
      }
      if (this._zoomPosition.y + size.h * this._scale < size.h) {
        this._zoomPosition.y = -size.h * (this._scale - 1);
      }

      update();
    };
    this._digiZoomInited = true;
  }

  private _hasRecordings(date: Date): boolean {
    const t = this.cameraTimeline;
    const testDate = new Date(date.getTime());
    const dateStart = testDate.getTime();
    testDate.setDate(testDate.getDate() + 1);
    const dateEnd = testDate.getTime();
    const timeline = this.hasFmp4
      ? t[0].record.filter((stream) => {
          if (typeof stream.start === 'string') {
            const start = parseISO(stream.start);
            const end = parseISO(stream.end);

            const startUnix = getUnixTime(start);
            const endUnix = getUnixTime(end);

            return dateStart < endUnix * 1000 && startUnix * 1000 < dateEnd;
          }
          return dateStart < stream.end * 1000 && stream.start * 1000 < dateEnd;
        })
      : t.timeline.filter((stream) => {
          return dateStart < stream.end * 1000 && stream.start * 1000 < dateEnd;
        });
    return timeline.length > 0;
  }

  private _updateTime(time: number): void {
    this.currentTime = time;
    if (!this.startingTime) {
      this.startingTime = time;
    }
    if (this._player.live) {
      this.time = new Date();
      this._player.timeStamp = this.time;
      this._changeDetectorRef.detectChanges();
      return;
    }
    if (time && this._startTime && !this.isDragging && !this._isUserSeeking) {
      this.time = new Date(this._startTime.getTime() + time * 1000);
      this._player.timeStamp = this.time;
      this._changeDetectorRef.detectChanges();
    } else {
      this._player.timeStamp = this.time;
      this._changeDetectorRef.detectChanges();
    }
    this.currentTimelineTime.next(this.time);
  }

  private _getTimeline(): void {
    if (
      this.isUnmanagedStreamActive ||
      (!this._camera.record && !this._camera.playbackUrls) ||
      this.camera.deepIntegrationMake === 'bosch'
    ) {
      return;
    }
    const urlSrc =
      this.hasFmp4 || this.isFlv
        ? this.camera.prerecordingUrls?.management
        : this.camera.playbackUrls?.main?.local?.management;
    this.isLoadingTimeline = true;
    this._timelineService
      .getTimelineData(urlSrc, { camera_id: this.camera.id, source: TIMELINE_SOURCE_LOCAL })
      .pipe(timeout(6000), takeUntil(this._destroy$))
      .subscribe({
        next: (data: any) => {
          this.isLocal = true;
          this.cameraTimeline = this.hasFmp4 ? data.result.timelines : data[0];
          this.timeline.next(this.cameraTimeline);
          this._initDigiZoom(100, 0.1);
          if (this.camTime) {
            this.setCamTime(this.camTime);
          }
          this._initTimelineInterval();
          this.isLoadingTimeline = false;
          this._changeDetectorRef.detectChanges();
        },
        error: () => {
          this.isLoadingTimeline = false;
        },
      });
  }

  private _loadCameraSettings(camera: SnCamera): any {
    return this._camerasAPI.getCameraSettings(camera.id);
  }

  private _getTimelineUrlSrc(): string {
    // For FMP4 or FLV streams (except Avigilon), use prerecording management URL
    if ((this.hasFmp4 || this.isFlv) && this.camera.external.externalType !== 'avigilon') {
      return this.camera.prerecordingUrls.management;
    }

    // For Avigilon systems, use the first timeline URL
    if (this.camera.external.externalType === 'avigilon') {
      return this.camera.prerecordingUrls.timeline[0];
    }

    // Default: use cloud management URL
    return this.camera.playbackUrls.main.cloud.management;
  }

  private _setCloudOrLocalTimeline(): void {
    if (this._isUserSeeking || this.isLoadingTimeline) {
      return;
    }
    this.localPlaying ? this._getTimeline() : this._getCloudTimeline();
  }

  private _getCloudTimeline(): void {
    if (
      this.isUnmanagedStreamActive ||
      (!this._camera.record && !this._camera.playbackUrls && !this._camera.prerecordingUrls) ||
      this.camera.deepIntegrationMake === 'bosch'
    ) {
      return;
    }
    const urlSrc = this._getTimelineUrlSrc();
    this.isLoadingTimeline = true;
    this._timelineService
      .getTimelineData(urlSrc, { camera_id: this.camera.id, source: TIMELINE_SOURCE_CLOUD })
      .pipe(timeout(6000), retry(3), takeUntil(this._destroy$))
      .subscribe({
        next: (data: any) => {
          this.isLocal = false;
          this.cameraTimeline = this.hasFmp4 ? data.result.timelines : data[0];
          this.timeline.next(this.cameraTimeline);
          this._initDigiZoom(100, 0.1);
          if (this.camTime) {
            this.setCamTime(this.camTime);
          }
          this._initTimelineInterval();
          this.isLoadingTimeline = false;
          this._changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this._emitPlaybackAttemptEnd('fail', {
            raw_error: `Timeline API error: ${
              error?.message || error?.statusText || 'Unknown error'
            }`,
            error_code: error?.status
              ? `HTTP_${error.status}`
              : error?.code || 'TIMELINE_API_ERROR',
            error_source: 'camera_stream_view',
          });

          this._getTimeline();
        },
      });
  }

  private _initTimelineInterval(): void {
    clearInterval(this._timelineUpdateInterval);
    if (!this.isFullscreen) {
      return;
    }
    this._timelineUpdateInterval = setInterval(() => {
      this._setCloudOrLocalTimeline();
    }, 5 * 60000);
  }

  private _initLTETimer(): void {
    timer(60000)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._player.pause();
        this._toggleConfirmAlertVisibility(false);
      });
  }

  private _toggleConfirmAlertVisibility(active: boolean): void {
    this.isPlayingConfirmed = active;
    this._changeDetectorRef.detectChanges();
  }

  private _shareTo(channel: SnChannel): void {
    this._chatMessageService
      .shareMediaMessage(channel, this.camera.name, {
        type: 'camera',
        cameraId: this.camera.id,
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this._toast.success('Camera Shared');
      });
  }

  private _checkIfImageSource(sources: SnStreamSources): boolean {
    if (!sources) {
      return this.isUnmanagedStreamActive && !this.camera.unmanagedLiveUrls;
    }
    return (
      !!sources.jpeg &&
      !sources.dash &&
      !sources.fmp4 &&
      !sources.hls &&
      !sources.mp4 &&
      !sources.rtmp &&
      !sources.rtsp &&
      !sources.sldp
    );
  }

  private _genFrameSrc(liveUrls: SnLiveVideoUrls): string {
    if (!liveUrls) {
      return this._camera.unmanagedUrl;
    }
    return liveUrls?.main?.cloud?.jpeg;
  }

  private _formatLabel(value: number): any {
    return value === 1 ? 'Normal' : value;
  }

  private _getNewUrl(): void {
    this.playStarted = false;
    this._camerasAPI
      .getCamera(this._camera.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe((cam) => {
        this.src = cam.liveVideoUrls;
        this._changeDetectorRef.detectChanges();
        this.urlUpdated = true;
        this._resetUrlUpdated();
      });
  }

  private _resetUrlUpdated(): void {
    timer(340 * 60 * 1000)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.urlUpdated = false;
      });
  }

  private _findAndSetPauseTime(): void {
    this._setCloudOrLocalTimeline();
    this.timeline.pipe(take(1), takeUntil(this._destroy$)).subscribe((res) => {
      this.cameraTimeline = res;
      if (!this.pausedTimeline) {
        this.date = addSeconds(new Date(), -60);
        this.pausedTimeline = true;
        this.autoplay = false;
        this.timelineTime = this.date;
      }
      this._changeDetectorRef.detectChanges();
    });
  }

  private _initTimeoutTimer(): void {
    if (this._currentUser.isVideoWallUser || this.isGridCam || this.isLocal) {
      return;
    }
    this.isTimerInited = true;
    timer(0, 1000)
      .pipe(
        takeUntil(this._stopTimer$),
        takeUntil(this._destroy$),
        repeatWhen(() => this._startTimer$),
      )
      .subscribe((time) => {
        const now = time * 1000;
        if (now >= this._cloudVideoTimeout * 60000 && !this.isLocal && !this.localPlaying) {
          this.timeoutStream = true;
          this._stopTimer$.next();

          this._emitPlaybackAttemptEnd('fail', {
            raw_error: `Cloud video timeout after ${this._cloudVideoTimeout} minutes`,
            error_code: `CLOUD_VIDEO_TIMEOUT_${this._cloudVideoTimeout}MIN`,
            error_source: 'camera_stream_view',
          });

          this._openTimeoutDialog();
        }
      });
  }

  private _openTimeoutDialog(): void {
    this._dialogService
      .timeoutMessage({
        data: {
          message: `
            Your profile is set to timeout after ${this._cloudVideoTimeout} minutes, continue?
          `,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((confirm) => {
        if (!confirm) {
          // User chose not to continue - this is a manual cancellation
          this._emitPlaybackAttemptEnd('canceled', {
            is_canceled: true,
          });

          this._player.destroyStream();
          this.toggleFullscreen();
          this.isSessionActive = false;
        } else {
          this.timeoutStream = false;
          this._startTimer$.next();
          this.isSessionActive = true;
        }
      });
  }

  private _loadCameraTime(date: Date): void {
    if (this.timelineTime) {
      this.loaded = false;
    }
    this.startingTime = null;
    this.time = date;
    const timelines = this.cameraTimeline?.timeline;
    const time = new Date(this._camTime);
    const timeSec = time.getTime() / 1000;
    const currentTimeline = timelines.find((t) => {
      return timeSec >= t.start && timeSec <= t.end;
    });
    this.activateSegment(currentTimeline);
    this.moveCursor(this.time);
    if (!this.timelineTime && !this.pauseOnTimeChange) {
      this._player.play();
    }
    if (this.pauseOnTimeChange) {
      setTimeout(() => {
        this._player.pause();
      }, 500);
    }
    this._changeDetectorRef.detectChanges();
  }

  private _initLiveUpdate(): void {
    this._liveUpdateSwitchService.initLiveTracking().then(() => {
      this._initLiveUpdateSubscriptions();
    });
  }

  private _initLiveUpdateSubscriptions(): void {
    this._liveUpdateSwitchService
      .subscribeToChannel({
        channel: `${this._currentOrganization.id}:cameras`,
        withHandle: true,
        filter: ({ data }) => data?.id === this.camera?.id,
        callback: ({ name, data }: { name: string; data: any }) => {
          switch (name) {
            case 'share:restrictionLifted':
            case 'share:restricted':
              this._camerasAPI
                .getCamera(data.id)
                .pipe(takeUntil(this._destroy$))
                .subscribe((camera) => {
                  this.camera = camera;
                  this._changeDetectorRef.detectChanges();
                });
              break;
          }
        },
      })
      .then((id) => (this._liveUpdateSubscriptionId = id as string));
  }

  private _unsubscribeFromLiveUpdate(): void {
    if (this._liveUpdateSubscriptionId) {
      this._liveUpdateSwitchService.unsubscribeById(this._liveUpdateSubscriptionId);
      this._liveUpdateSubscriptionId = null;
    }
    // Do not call channel-wide unsubscribe here to avoid impacting other listeners (e.g. map markers)
  }

  private _setActivePlayer(player: ISnPlayer, isActive: boolean): void {
    const videoPlayer = player as any;
    if (videoPlayer?.reference instanceof SnSldpPlayerReference) {
      videoPlayer.reference.isActive = isActive;
    }
  }
}
